import { useState, useCallback, useMemo, useRef, useEffect } from 'react'

export type SortDirection = 'asc' | 'desc' | null

export interface TableSortState {
  columnKey: string | null
  direction: SortDirection
}

export interface TableSettings {
  sort: TableSortState
  visibleColumns: Record<string, boolean>
  filters: Record<string, string | string[]>
  /** Maps filter value IDs to human-readable labels (e.g. userId → "John Doe") */
  filterLabels?: Record<string, Record<string, string>>
}

const getStorageKey = (tableId: string, type: 'settings') => `table-${type}-${tableId}`

const loadSettings = (tableId: string): Partial<TableSettings> => {
  try {
    const item = window.localStorage.getItem(getStorageKey(tableId, 'settings'))
    if (item) {
      return JSON.parse(item)
    }
  } catch (error) {
    console.warn(`Error reading table settings for "${tableId}":`, error)
  }
  return {}
}

const saveSettings = (tableId: string, settings: TableSettings): void => {
  try {
    window.localStorage.setItem(getStorageKey(tableId, 'settings'), JSON.stringify(settings))
  } catch (error) {
    console.warn(`Error saving table settings for "${tableId}":`, error)
  }
}

export interface UseTableSettingsOptions {
  tableId: string
  defaultVisibleColumns?: Record<string, boolean>
  defaultSort?: TableSortState
}

export interface UseTableSettingsReturn {
  // Sort state
  sortColumn: string | null
  sortDirection: SortDirection
  toggleSort: (columnKey: string) => void
  setSort: (columnKey: string | null, direction: SortDirection) => void

  // Column visibility
  isColumnVisible: (columnKey: string) => boolean
  toggleColumnVisibility: (columnKey: string) => void
  setColumnVisibility: (columnKey: string, visible: boolean) => void

  // Column filters (text)
  getColumnFilter: (columnKey: string) => string
  setColumnFilter: (columnKey: string, value: string) => void
  // Column filters (array/enum)
  getColumnFilterArray: (columnKey: string) => string[]
  setColumnFilterArray: (columnKey: string, value: string[]) => void
  toggleColumnFilterArrayItem: (columnKey: string, item: string) => void
  // Common
  clearColumnFilter: (columnKey: string) => void
  clearAllSettings: () => void
  hasActiveSettings: () => boolean

  // Raw state (for filter set manager)
  filters: Record<string, string | string[]>
  visibleColumns: Record<string, boolean>
  filterLabels: Record<string, Record<string, string>>
  setFilterLabel: (filterKey: string, value: string, label: string) => void
  applyFilterSet: (sort: TableSortState, filters: Record<string, string | string[]>, visibleColumns?: Record<string, boolean>, filterLabels?: Record<string, Record<string, string>>) => void

  // Utility
  getSortState: (columnKey: string) => SortDirection
}

/**
 * Custom hook for managing table column sorting and visibility with localStorage persistence
 * @param options - Configuration options for the hook
 * @returns Object with sort and visibility management functions
 */
export const useTableSettings = (options: UseTableSettingsOptions): UseTableSettingsReturn => {
  const { tableId, defaultVisibleColumns = {}, defaultSort = { columnKey: null, direction: null } } = options

  // Load initial state from localStorage
  const loadedSettings = useMemo(() => loadSettings(tableId), [tableId])

  const [sortState, setSortState] = useState<TableSortState>(() =>
    loadedSettings.sort || defaultSort
  )

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    // Merge defaults with loaded settings, with loaded settings taking precedence
    return { ...defaultVisibleColumns, ...loadedSettings.visibleColumns }
  })

  const [filters, setFilters] = useState<Record<string, string | string[]>>(() =>
    loadedSettings.filters || {}
  )

  const [filterLabels, setFilterLabels] = useState<Record<string, Record<string, string>>>(() =>
    loadedSettings.filterLabels || {}
  )

  // Persist to localStorage whenever any state changes
  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    saveSettings(tableId, { sort: sortState, visibleColumns, filters, filterLabels })
  }, [tableId, sortState, visibleColumns, filters, filterLabels])

  const toggleSort = useCallback((columnKey: string) => {
    setSortState(prev => {
      if (prev.columnKey === columnKey) {
        // Same column: asc → desc → clear (third click)
        if (prev.direction === 'asc') {
          return { columnKey, direction: 'desc' }
        } else if (prev.direction === 'desc') {
          return { columnKey: null, direction: null }
        } else {
          // null -> start with asc
          return { columnKey, direction: 'asc' }
        }
      } else {
        // Different column: start with asc
        return { columnKey, direction: 'asc' }
      }
    })
  }, [])

  const setSort = useCallback((columnKey: string | null, direction: SortDirection) => {
    setSortState({ columnKey, direction })
  }, [])

  const isColumnVisible = useCallback((columnKey: string): boolean => {
    // If not in the record, default to visible
    return visibleColumns[columnKey] !== false
  }, [visibleColumns])

  const toggleColumnVisibility = useCallback((columnKey: string) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: prev[columnKey] === false }))
  }, [])

  const setColumnVisibility = useCallback((columnKey: string, visible: boolean) => {
    setVisibleColumns(prev => ({ ...prev, [columnKey]: visible }))
  }, [])

  const getColumnFilter = useCallback((columnKey: string): string => {
    const filter = filters[columnKey]
    return typeof filter === 'string' ? filter : ''
  }, [filters])

  const getColumnFilterArray = useCallback((columnKey: string): string[] => {
    const filter = filters[columnKey]
    return Array.isArray(filter) ? filter : []
  }, [filters])

  const setColumnFilter = useCallback((columnKey: string, value: string) => {
    setFilters(prev => ({ ...prev, [columnKey]: value }))
  }, [])

  const setColumnFilterArray = useCallback((columnKey: string, value: string[]) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      if (value.length > 0) {
        newFilters[columnKey] = value
      } else {
        delete newFilters[columnKey]
      }
      return newFilters
    })
  }, [])

  const toggleColumnFilterArrayItem = useCallback((columnKey: string, item: string) => {
    setFilters(prev => {
      const current = Array.isArray(prev[columnKey]) ? prev[columnKey] as string[] : []
      const newArray = current.includes(item)
        ? current.filter(v => v !== item)
        : [...current, item]
      const newFilters = { ...prev }
      if (newArray.length > 0) {
        newFilters[columnKey] = newArray
      } else {
        delete newFilters[columnKey]
      }
      return newFilters
    })
  }, [])

  const clearColumnFilter = useCallback((columnKey: string) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[columnKey]
      return newFilters
    })
  }, [])

  const setFilterLabel = useCallback((filterKey: string, value: string, label: string) => {
    setFilterLabels(prev => {
      if (prev[filterKey]?.[value] === label) return prev
      return { ...prev, [filterKey]: { ...prev[filterKey], [value]: label } }
    })
  }, [])

  const getSortIcon = useCallback((columnKey: string): SortDirection => {
    if (sortState.columnKey === columnKey) {
      return sortState.direction
    }
    return null
  }, [sortState])

  const applyFilterSet = useCallback((newSort: TableSortState, newFilters: Record<string, string | string[]>, newVisibleColumns?: Record<string, boolean>, newFilterLabels?: Record<string, Record<string, string>>) => {
    setSortState(newSort)
    setFilters(newFilters)
    if (newFilterLabels) {
      setFilterLabels(newFilterLabels)
    }
    if (newVisibleColumns) {
      setVisibleColumns(newVisibleColumns)
    }
  }, [])

  const clearAllSettings = useCallback(() => {
    setSortState(defaultSort)
    setVisibleColumns({ ...defaultVisibleColumns })
    setFilters({})
  }, [defaultSort, defaultVisibleColumns])

  const hasActiveSettings = useCallback(() => {
    // Check if there's an active sort
    if (sortState.columnKey !== null) {
      return true
    }

    // Check if there are any active filters
    if (Object.keys(filters).length > 0) {
      return true
    }

    // Check if any columns are hidden (different from defaults)
    for (const key in defaultVisibleColumns) {
      if (visibleColumns[key] !== defaultVisibleColumns[key]) {
        return true
      }
    }
    // Also check for columns that might be in visibleColumns but not in defaults
    for (const key in visibleColumns) {
      if (defaultVisibleColumns[key] === undefined && visibleColumns[key] === false) {
        return true
      }
    }

    return false
  }, [sortState.columnKey, filters, visibleColumns, defaultVisibleColumns])

  return {
    sortColumn: sortState.columnKey,
    sortDirection: sortState.direction,
    toggleSort,
    setSort,
    isColumnVisible,
    toggleColumnVisibility,
    setColumnVisibility,
    getColumnFilter,
    setColumnFilter,
    getColumnFilterArray,
    setColumnFilterArray,
    toggleColumnFilterArrayItem,
    clearColumnFilter,
    clearAllSettings,
    hasActiveSettings,
    filters,
    visibleColumns,
    filterLabels,
    setFilterLabel,
    applyFilterSet,
    getSortState: getSortIcon,
  }
}
